import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useState, useRef } from 'react';
// Blueprint FormGroup and Spinner removed — using Skyline equivalents
import { useNavigate } from 'react-router-dom';
import { toast as message } from 'sonner';

import { useFieldDependentTable } from '../utils/metadata/tables';
import { useGlobalMetadata } from '../utils/providers';
import { supabaseClient } from '../utils/supabase';
import { deleteRecord, handleOperationError, updateRecord } from '../utils/supabase-operations';
import { activityTracker } from '../utils/activityTracker';
import { isValidEmail, normalizeEmail } from '../utils/validation';
import { valuesAreEqual } from '../utils/common';
import { queryPolicies } from '../utils/queryPolicies';

import { useEditLocks } from '../hooks/useEditLocks';
import RecordFieldInput from './RecordDetailForm/components/RecordFieldInput';
import RecordDeleteDialog from './RecordDetailForm/components/RecordDeleteDialog';
import RecordDeleteBanner from './RecordDetailForm/components/RecordDeleteBanner';
import RecordDeleteButton from './RecordDetailForm/components/RecordDeleteButton';
import RecordWarningAlert from './RecordDetailForm/components/RecordWarningAlert';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { useRecordFormState } from './RecordDetailForm/hooks/useRecordFormState';
import { useRecordRealtimeSync } from './RecordDetailForm/hooks/useRecordRealtimeSync';
import { useFieldUpdateHandlers } from './RecordDetailForm/hooks/useFieldUpdateHandlers';
import { useDeletedRecord } from './RecordDetailForm/hooks/useDeletedRecord';
import { buildRecordFormMetadata } from './RecordDetailForm/utils/metadataUtils';

import type {
  EnhancedFormFieldValues,
  RecordDetailFormHandle,
  RecordDetailFormProps,
  LegacyFormFieldObject,
  LegacyFormSection
} from '../types/record-form';
import type { DynamicRecord } from '../types/api';
import type { FormFieldValue } from '../types/forms';
import type { EnrichedTableField } from '../types/metadata';

import './Common.css';

const formSkeletonBlock = (key: string, width: string) => (
  <div key={key} style={{ display: 'grid', gap: 7 }}>
    <div style={{ width: '32%', height: 10, borderRadius: 4, background: '#e7e9ed' }} />
    <div style={{ width, height: 38, borderRadius: 7, background: '#f0f1f3' }} />
  </div>
);

/** Keeps the record layout stable while its fields are being fetched. */
function RecordFormSkeleton(): React.ReactElement {
  return (
    <div aria-label="Loading record" aria-busy="true" style={{ display: 'grid', gap: 16 }}>
      {[0, 1, 2].map(section => (
        <div key={section} style={{ border: '1px solid var(--border-soft)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ height: 36, background: '#f6f7f9', borderBottom: '1px solid var(--border-soft)' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, padding: 16 }}>
            {formSkeletonBlock(`${section}-a`, '100%')}
            {formSkeletonBlock(`${section}-b`, '100%')}
            {formSkeletonBlock(`${section}-c`, '100%')}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * One collapsible section of the `cards` layout.
 *
 * Records like a submission carry far more columns than are relevant at any one
 * deal stage — funding details mean nothing until a deal funds. Each section
 * states what it is for and stays shut until the user (or `defaultOpen`) asks
 * for it, so the form opens on the fields that actually apply.
 */
function FormSectionCard({
  section,
  extra,
  children,
}: {
  section: LegacyFormSection;
  extra?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(section.defaultOpen !== false);

  // `defaultOpen` is derived from record state (a funded deal opens its funding
  // section), so it can flip after the record loads or its status changes.
  useEffect(() => {
    setOpen(section.defaultOpen !== false);
  }, [section.defaultOpen]);

  // The accent marks the stage a section belongs to, but only once it is open —
  // a collapsed form stays a calm list instead of a set of coloured stripes.
  const accent = open && section.accent ? ` is-accent-${section.accent}` : '';

  return (
    <section className={`sky-form-card${open ? ' is-open' : ''}${accent}`}>
      <button
        type="button"
        className="sky-form-card-head"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <svg className="sky-form-card-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18l6-6-6-6" />
        </svg>
        <span className="sky-form-card-label">{section.label}</span>
        {section.badge?.count != null && (
          <span className="sky-form-card-count">{section.badge.count}</span>
        )}
        {section.badge?.text && <span className="sky-form-card-hint">{section.badge.text}</span>}
        {section.hint && <span className="sky-form-card-hint">{section.hint}</span>}
      </button>
      {open && (
        <div className="sky-form-card-body">
          <div className="sky-form-card-grid">{children}</div>
          {extra}
        </div>
      )}
    </section>
  );
}

function getRecordEmailForGmailKeepAlive(
  record: DynamicRecord,
  tableName: string,
  metadata: ReturnType<typeof useGlobalMetadata>['data'] | undefined
): string | null {
  const tableInfo = metadata?.tableData?.byName?.[tableName];
  if (!tableInfo?.id) return null;

  const keepAliveMap = metadata?.globalSettingsData?.gmail_keep_alive_table_field_map || {};
  const keepAliveFieldIds = keepAliveMap[String(tableInfo.id)] || [];
  if (!keepAliveFieldIds.length) return null;

  for (const fieldId of keepAliveFieldIds) {
    const field = metadata?.fieldData?.byId?.[fieldId] as EnrichedTableField | undefined;
    if (!field?.name || !field.is_email || field.table_info !== tableInfo.id) continue;

    const value = record[field.name];
    if (isValidEmail(value)) {
      return normalizeEmail(value);
    }
  }

  return null;
}

/**
 * Dynamic record detail form component that renders form fields based on metadata configuration.
 *
 * Key Features:
 * - Dynamic field rendering based on database metadata
 * - Optimistic updates with debouncing for text fields
 * - Relationship field support (client-side and server-side lookups)
 * - Multi-select and single-select picklist support
 * - Real-time data synchronization
 * - Comprehensive error handling with user-friendly messages
 * - Activity tracking for user interactions
 *
 * @param props - Component props including tab configuration and record ID
 */
const RecordDetailForm = forwardRef<RecordDetailFormHandle, RecordDetailFormProps>(({
  tabConfig,
  recordId,
  readonly = false,
  manualSave = false,
  className = '',
  onDeleted,
  sectionVariant = 'flat',
  sectionExtras,
  hideChrome = false,
  onDraftChange,
}, ref) => {
  const { table_name: tableName, schema_name } = tabConfig;
  // A layout can still name a field whose metadata was deleted (REG-895). Skip
  // it rather than let one stale entry take down the whole record page.
  const sections = useMemo(() => tabConfig.sections.map((section: LegacyFormSection) => {
    const fields = section.fields.filter((fieldObj) => !!fieldObj.dbCol);
    if (fields.length !== section.fields.length) {
      const missing = section.fields.filter((fieldObj) => !fieldObj.dbCol).map((fieldObj) => fieldObj.field);
      console.warn(`[RecordDetailForm] ${tableName} layout names fields with no metadata, skipped: ${missing.join(', ')}`);
    }
    return fields.length === section.fields.length ? section : { ...section, fields };
  }), [tabConfig.sections, tableName]);
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  // A caller owning the chrome also owns the mode: there is no Edit button to
  // press, so the fields are live from the moment the record lands.
  const isEditing = manualSave ? (hideChrome || editing) : editing;

  // Live edit locks, per field. Entering a field claims it; two reps can still work
  // different fields of the same record, which is how the form is used. When the form
  // autosaves, leaving a field releases it, because the value is already written. Under
  // manual save the lock instead spans the edit: it is held from the first change until
  // the save or discard that resolves it, so a staged value is never advertised as free.
  const [, bumpLocks] = useReducer((n: number) => n + 1, 0);
  const { getLockOwner, acquire, release } = useEditLocks({
    schema: schema_name,
    tableName,
    onChange: bumpLocks,
    enabled: recordId != null,
  });

  // Global metadata hook
  const {
    data: metadata,
    error: metadataError,
    isLoading: metadataIsLoading
  } = useGlobalMetadata();

  // Record fetching configuration
  const getRecordArgs = useMemo(() => ({
    tableName,
    conditions: {
      id: {
        filterType: 'number' as const,
        type: 'equals' as const,
        filter: recordId,
        colId: 'id',
      },
    },
    limitOffset: {
      limit: 1,
      offset: 0,
    },
    withCount: false,
    singleRow: true,
  }), [tableName, recordId]);

  // Fetch record data
  const {
    data: record,
    error,
    isLoading,
    isFetching,
  } = useFieldDependentTable({
    enabled: !!recordId,
    schema: schema_name,
    deep: true,
    staleTime: queryPolicies.recordDetail.staleTime,
    // A refetch rebuilds `fieldValues` from the server row, which would discard
    // edits that manual-save mode is deliberately holding back. Pause the
    // focus refetch while those edits are outstanding.
    refetchOnWindowFocus: !(manualSave && isEditing),
    ...getRecordArgs
  });

  // Track if we've ever successfully loaded this record.
  // Set synchronously during render (not in useEffect) so it's available
  // on the very same render cycle — prevents race with cache invalidation.
  const hadRecordRef = useRef(false);
  if (record) {
    hadRecordRef.current = true;
  }

  // Grace period: never show "Not Found" within the first second after mount.
  // This prevents false "Not Found" flashes caused by React Query returning
  // stale/corrupted cache data for one render frame before the real fetch starts.
  const [mountGraceExpired, setMountGraceExpired] = useState(false);
  useEffect(() => {
    setMountGraceExpired(false);
    const timer = setTimeout(() => setMountGraceExpired(true), 1000);
    return () => clearTimeout(timer);
  }, [recordId]);

  // Check if record was deleted using SECURITY DEFINER function
  // This allows all users to see deletion status without exposing audit data
  // Optimistically fetch deleted record data - RLS only returns for admins
  const {
    wasDeleted,
    isLoadingAudit,
    deletedRecordData
  } = useDeletedRecord({
    tableName,
    schemaName: schema_name,
    recordId,
    isEnabled: !isLoading && !record && !hadRecordRef.current && !!recordId && metadata?.tableData?.byName?.[tableName]?.audit_logs === true
  });

  // Also track deletedRecordData as "had record"
  if (deletedRecordData) {
    hadRecordRef.current = true;
  }
  const gmailKeepAliveSentRef = useRef<Set<string>>(new Set());

  // Use deleted record data for admins viewing deleted records
  // This allows admins to see grayed-out form with deleted data
  const displayRecord = deletedRecordData || record;
  const isViewingDeletedRecord = !!deletedRecordData;

  // Extract metadata for current table
  const processedMetadata = useMemo(() => {
    return buildRecordFormMetadata(metadata, tableName);
  }, [metadata, tableName]);

  const [debouncedFieldValues, setDebouncedFieldValues] = useState<Record<string, FormFieldValue>>({});
  const [debouncedPreviousValues, setDebouncedPreviousValues] = useState<Record<string, FormFieldValue>>({});
  const [fieldsBeingSaved, setFieldsBeingSaved] = useState<Set<string>>(new Set());
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const originalFieldValuesRef = useRef<EnhancedFormFieldValues>({});
  const baselineReadyRef = useRef(false);
  const manualDirtyFieldsRef = useRef<Set<string>>(new Set());
  // Every live-save field this record's layout has shown. Sticky because a
  // conditional section can drop one (Missing documents leaves the layout when
  // the status moves off Held by docs), and Save must still skip it.
  const liveSaveFieldsRef = useRef<{ recordId: typeof recordId; names: Set<string> }>({ recordId, names: new Set() });

  useEffect(() => {
    baselineReadyRef.current = false;
    manualDirtyFieldsRef.current.clear();
  }, [recordId]);

  const {
    fieldValues,
    setFieldValues,
    fieldErrors,
    setFieldErrors,
    flashingFields
  } = useRecordFormState({
    record: displayRecord,
    picklists: processedMetadata.picklists,
    debouncedFieldValues,
    fieldsBeingSaved,
    tableName,
    fieldData: metadata?.fieldData,
    // A realtime refetch must not flow into the editable snapshot while a manual-save
    // form has staged work. Otherwise trigger-owned server changes look like local edits
    // against the older baseline and manufacture a stale-write conflict on Save.
    preserveRecordValues: manualSave && (
      hideChrome ? manualDirtyFieldsRef.current.size > 0 : editing
    ),
  });

  const setTrackedFieldValues = useCallback<React.Dispatch<React.SetStateAction<EnhancedFormFieldValues>>>((action) => {
    setFieldValues((previous) => {
      const next = typeof action === 'function' ? action(previous) : action;
      if (manualSave && baselineReadyRef.current) {
        const fields = new Set([...Object.keys(previous), ...Object.keys(next)]);
        fields.forEach((fieldName) => {
          // Live-save edits are persisted on change, so they are never staged work.
          if (liveSaveFieldsRef.current.names.has(fieldName)) return;
          if (valuesAreEqual(next[fieldName], originalFieldValuesRef.current[fieldName])) {
            manualDirtyFieldsRef.current.delete(fieldName);
          } else {
            manualDirtyFieldsRef.current.add(fieldName);
          }
        });
      }
      return next;
    });
  }, [manualSave, setFieldValues]);

  const {
    warningState,
    confirmWarning,
    cancelWarning,
    handleFieldUpdate,
    debouncedHandleFieldUpdate,
    debouncedEmailUpdate
  } = useFieldUpdateHandlers({
    tableName,
    schemaName: schema_name,
    recordId,
    getRecordArgs,
    processedMetadata,
    fieldValues,
    setFieldValues,
    setFieldErrors,
    debouncedFieldValues,
    setDebouncedFieldValues,
    debouncedPreviousValues,
    setDebouncedPreviousValues,
    fieldsBeingSaved,
    setFieldsBeingSaved
  });

  const cancelDebouncedField = useCallback((fieldName: string) => {
    setDebouncedFieldValues((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, [setDebouncedFieldValues]);

  useEffect(() => {
    if (!manualSave || !displayRecord) return;
    if (hideChrome) {
      // Nothing gates edit mode here. Keep following server refreshes until the first
      // genuine local edit, then freeze the baseline until save/discard. Advancing it
      // while clean prevents trigger-owned fields from being mistaken for local edits.
      if (manualDirtyFieldsRef.current.size === 0 && Object.keys(fieldValues).length > 0) {
        baselineReadyRef.current = true;
        originalFieldValuesRef.current = { ...fieldValues };
      }
      return;
    }
    if (!editing) {
      originalFieldValuesRef.current = { ...fieldValues };
    }
  }, [displayRecord, editing, fieldValues, hideChrome, manualSave]);

  const stageFieldUpdate = useCallback((fieldName: string, value: FormFieldValue) => {
    setTrackedFieldValues((prev) => ({ ...prev, [fieldName]: value } as EnhancedFormFieldValues));
    onDraftChange?.(fieldName, value);
  }, [onDraftChange, setTrackedFieldValues]);

  // Fields a manual-save layout marks `liveSave` write through the standard
  // per-field autosave path instead of being staged, so their edits are
  // already persisted when the modal closes. Save and discard leave them
  // alone - they are outside the manual-save contract.
  const liveSaveFields = useMemo(() => {
    if (liveSaveFieldsRef.current.recordId !== recordId) {
      liveSaveFieldsRef.current = { recordId, names: new Set() };
    }
    const names = liveSaveFieldsRef.current.names;
    sections.forEach((section: LegacyFormSection) => {
      section.fields.forEach((fieldObj) => {
        if (fieldObj.liveSave) names.add(fieldObj.field);
      });
    });
    return new Set(names);
  }, [recordId, sections]);

  const discardEdits = useCallback(() => {
    manualDirtyFieldsRef.current.clear();
    // Live-save fields are already persisted; rolling them back to the staged
    // baseline would show values the database no longer holds.
    setFieldValues((prev) => {
      const next = { ...originalFieldValuesRef.current } as EnhancedFormFieldValues;
      liveSaveFields.forEach((fieldName) => {
        if (fieldName in prev) next[fieldName] = prev[fieldName];
      });
      return next;
    });
    setFieldErrors({});
    Object.keys(processedMetadata.fields || {}).forEach(fieldName => release(recordId, fieldName));
    setEditing(false);
  }, [liveSaveFields, processedMetadata.fields, recordId, release, setFieldErrors, setFieldValues]);

  const saveEdits = useCallback(async () => {
    const patch: Record<string, any> = {};
    const expectedValues: Record<string, any> = {};
    const expectedValueTypes: Record<string, string> = {};

    Object.entries(fieldValues).forEach(([fieldName, value]) => {
      const fieldDef = processedMetadata.fields?.[fieldName];
      if (!fieldDef || fieldDef.is_calculated || fieldDef.is_system) return;
      // Already persisted through the autosave path; re-writing it here would
      // trip the stale-write guard against its own earlier write.
      if (liveSaveFields.has(fieldName)) return;

      const previousValue = originalFieldValuesRef.current[fieldName];
      if (!valuesAreEqual(value, previousValue)) {
        const picklist = processedMetadata.picklists?.[fieldName];
        patch[fieldName] = picklist?.type === 'Multi Select'
          ? (Array.isArray(value) && value.length > 0 ? JSON.stringify(value) : null)
          : value;
        expectedValues[fieldName] = picklist?.type === 'Multi Select'
          ? (Array.isArray(previousValue) && previousValue.length > 0 ? JSON.stringify(previousValue) : null)
          : previousValue ?? null;
        if (fieldDef.data_type) expectedValueTypes[fieldName] = fieldDef.data_type;
      }
    });

    if (Object.keys(patch).length === 0) {
      discardEdits();
      return;
    }

    setIsSavingForm(true);
    try {
      const result = await updateRecord({
        tableName,
        schema: schema_name,
        data: patch,
        whereClause: { id: recordId },
        options: { fieldName: Object.keys(patch)[0], expectedValues, expectedValueTypes },
      });
      if (result.error) throw result.error;

      originalFieldValuesRef.current = { ...fieldValues };
      manualDirtyFieldsRef.current.clear();
      Object.keys(patch).forEach(fieldName => release(recordId, fieldName));
      setEditing(false);
      message.success('Saved');
    } catch (error) {
      const errorInfo = handleOperationError(error, `Failed to save ${tableName}`);
      message.error(errorInfo.userMessage);
    } finally {
      setIsSavingForm(false);
    }
  }, [discardEdits, fieldValues, liveSaveFields, processedMetadata.fields, processedMetadata.picklists, recordId, release, schema_name, tableName]);

  useImperativeHandle(ref, (): RecordDetailFormHandle => ({
    save: saveEdits,
    discard: discardEdits,
    requestDelete: () => setShowDeleteConfirmation(true),
    isSaving: () => isSavingForm,
  }), [discardEdits, isSavingForm, saveEdits]);

  const {
    isDeleted,
    showDeletionModal,
    deletionInfo,
    setShowDeletionModal
  } = useRecordRealtimeSync({
    tableName,
    recordId,
    getRecordArgs,
    schemaName: schema_name,
    metadata
  });

  // Activity tracking for user views
  // Only track views of live records, not deleted records
  useEffect(() => {
    if (displayRecord && !isViewingDeletedRecord && tableName && recordId) {
      // Handle both single record and array cases
      const recordData = Array.isArray(displayRecord) ? displayRecord[0] : displayRecord;
      if (!recordData) return;

      const getRecordDisplayName = (record: DynamicRecord, metadata: any): string => {
        const tableMetadata = metadata?.fieldData?.[tableName];
        if (!tableMetadata) return `Record ${record.id}`;

        // Look for common name fields in order of preference
        // TODO fix, we now have a name attribute on table_info (ui data i think)
        const nameFields = ['name', 'title', 'subject', 'business_name', 'first_name', 'email'];
        for (const field of nameFields) {
          if (record[field] && typeof record[field] === 'string') {
            return record[field] as string;
          }
        }
        return `Record ${record.id}`;
      };

      const displayName = getRecordDisplayName(recordData, metadata);
      activityTracker.trackUserView(tableName, recordId, displayName);

      const keepAliveEmail = getRecordEmailForGmailKeepAlive(recordData, tableName, metadata);
      const keepAliveKey = keepAliveEmail ? `${tableName}:${recordId}:${keepAliveEmail}` : null;
      if (
        keepAliveEmail &&
        keepAliveKey &&
        !gmailKeepAliveSentRef.current.has(keepAliveKey)
      ) {
        gmailKeepAliveSentRef.current.add(keepAliveKey);
        void supabaseClient.functions
          .invoke('gmail-keep-alive', { body: { email: keepAliveEmail } })
          .then(({ error }) => {
            if (error) {
              console.warn('Failed to refresh Gmail keep-alive for record email:', error);
            }
          })
          .catch((error) => {
            console.warn('Failed to refresh Gmail keep-alive for record email:', error);
          });
      }
    }
  }, [displayRecord, isViewingDeletedRecord, tableName, recordId, metadata]);

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteRecord({
        tableName,
        schema: schema_name,
        whereClause: { id: recordId }
      });

      // Check if the delete operation returned an error
      if (result.error) {
        throw result.error;
      }

      message.success(`${tableName} deleted successfully`);
      setShowDeleteConfirmation(false);

      if (onDeleted) {
        onDeleted();
      } else {
        const routeName = metadata?.routeData?.tableToRouteName?.[tableName];
        if (routeName) {
          navigate(`/${routeName}`);
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      const errorInfo = handleOperationError(error, `Failed to delete ${tableName}`);
      message.error(errorInfo.userMessage);
      console.error(`Error deleting ${tableName}:`, errorInfo.debugInfo);
      setIsDeleting(false);
    }
  };

  // Error handling
  if (error || metadataError) {
    return <div>Error: {error?.message || metadataError?.message}</div>;
  }

  // Loading state - preserve the final form footprint during initial load.
  const showSpinner = isLoading || metadataIsLoading ||
    (!record && !hadRecordRef.current && (isFetching || !mountGraceExpired));
  if (showSpinner) {
    return <RecordFormSkeleton />;
  }

  // Show spinner while checking if record was deleted
  // This prevents flash when transitioning from "not found" to "deleted" state
  if (isLoadingAudit) {
    return <RecordFormSkeleton />;
  }

  // Record not found or deleted
  // For admins with deleted record data, continue to form rendering (grayed out)
  // For non-admins or records without audit data, show error message
  // Only show "not found" after grace period, when not fetching, and never had record
  if (!record && !deletedRecordData && !hadRecordRef.current && mountGraceExpired && !isFetching) {
    // In side panel, just close the tab — no banner needed
    if (onDeleted) {
      onDeleted();
      return null;
    }

    const isRecordDeleted = wasDeleted === true;
    const intent = isRecordDeleted ? 'warning' : 'danger';
    const title = isRecordDeleted ? 'Record Deleted' : 'Record Not Found';
    const message = isRecordDeleted
      ? 'This record has been deleted and is no longer available. If you need to restore it, please contact your administrator.'
      : 'The requested record does not exist.';

    return (
      <div className="w-full max-w-4xl ml-3">
        <div className={`sky-callout sky-callout-${intent}`}>
          <h4 className="sky-callout-title">{title}</h4>
          <p>{message}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="sky-btn sky-btn-ghost" onClick={() => navigate(-1)}>Go Back</button>
            <button className="sky-btn sky-btn-ghost" onClick={() => {
              const routeName = metadata?.routeData?.tableToRouteName?.[tableName];
              navigate(routeName ? `/${routeName}` : '/');
            }}>Go to List</button>
          </div>
        </div>
      </div>
    );
  }

  // Render form sections and fields
  const renderField = (section: LegacyFormSection, fieldObj: LegacyFormFieldObject): React.ReactElement => {
    const { field, dbCol } = fieldObj;
    const fieldName = field;
    const uiLabel = fieldObj.label || dbCol.ui_label || fieldName;
    const key = fieldObj.field + section.label;
    const hasError = !!fieldErrors[fieldName];
    const lockOwner = recordId != null ? getLockOwner(recordId, fieldName) : null;
    // A live-save field in a manual-save form uses the autosave handlers.
    const isLiveField = manualSave && !!fieldObj.liveSave;
    // Calculated columns are written by the database, never by this form. The
    // cards layout says so inline so a user does not hunt for a disabled input.
    const isDerived = !!(dbCol.is_calculated || dbCol.is_system);

    return (
      // onFocus/onBlur are React's focusin/focusout, so they fire for whichever control
      // the field renders — one handler instead of threading focus through every input
      // variant in RecordFieldInput.
      <div
        key={key}
        className={`sky-form-group${hasError ? ' sky-form-group-error' : ''}${flashingFields.has(fieldName) ? ' field-flash' : ''}${lockOwner ? ' sky-form-group-locked' : ''}`}
        onFocus={() => { if (recordId != null && !lockOwner) acquire(recordId, fieldName); }}
        onBlur={() => {
          if (recordId == null) return;
          // Releasing on blur assumes the edit is already written, which is true
          // while the form autosaves. In manual-save mode the value is only
          // staged, so dropping the lock here would advertise the field as free
          // while this user still holds uncommitted work on it — and the other
          // writer would only find out when the stale-write guard rejects their
          // save. Hold it until saveEdits or discardEdits releases it.
          const staged = manualSave
            && !isLiveField // live-save writes land on change, so blur means done
            && isEditing
            && !valuesAreEqual(fieldValues[fieldName], originalFieldValuesRef.current[fieldName]);
          if (!staged) release(recordId, fieldName);
        }}
      >
        <label className="sky-form-label" htmlFor={fieldName}>
          {uiLabel}
          {fieldObj.requiredMark && <span className="sky-form-required-mark" aria-hidden="true">*</span>}
          {sectionVariant === 'cards' && isDerived && <span className="sky-form-auto-chip">auto</span>}
          {sectionVariant === 'cards' && fieldObj.note && <span className="sky-form-field-note">{fieldObj.note}</span>}
          {lockOwner && <span className="sky-form-lock-note"> — {lockOwner} is editing</span>}
        </label>
        <RecordFieldInput
          fieldObj={fieldObj}
          fieldKey={key}
          fieldValues={fieldValues}
          fieldErrors={fieldErrors}
          setFieldValues={manualSave ? setTrackedFieldValues : setFieldValues}
          setFieldErrors={setFieldErrors}
          processedMetadata={processedMetadata}
          readOnly={readonly || (manualSave && !isEditing) || isViewingDeletedRecord || Boolean(lockOwner)}
          displayOnly={manualSave && !isEditing}
          isDeleted={isDeleted}
          schemaName={schema_name}
          tableName={tableName}
          recordId={recordId}
          onFieldUpdate={manualSave && !isLiveField ? stageFieldUpdate : handleFieldUpdate}
          onDebouncedFieldUpdate={manualSave && !isLiveField ? stageFieldUpdate : debouncedHandleFieldUpdate}
          onDebouncedEmailUpdate={manualSave && !isLiveField ? stageFieldUpdate : debouncedEmailUpdate}
          onCancelDebouncedField={cancelDebouncedField}
        />
        {fieldErrors[fieldName] && <div className="sky-form-helper-error">{fieldErrors[fieldName]}</div>}
      </div>
    );
  };

  const formItems: React.ReactElement[] = [];

  if (sectionVariant === 'cards') {
    sections.forEach((section: LegacyFormSection) => {
      const fields = section.fields.map((fieldObj) => renderField(section, fieldObj));
      const extra = sectionExtras?.[section.label];

      if (section.flat) {
        formItems.push(
          <div key={section.label} className="sky-form-flat-section">
            <div className="sky-form-flat-label">{section.label}</div>
            <div className="sky-form-card-grid">{fields}</div>
            {extra}
          </div>
        );
        return;
      }

      formItems.push(
        <FormSectionCard key={section.label} section={section} extra={extra}>
          {fields}
        </FormSectionCard>
      );
    });
  } else {
    sections.forEach((section: LegacyFormSection) => {
      formItems.push(
        <div key={section.label} style={{ gridColumn: "1 / -1" }}>
          <h4 className="text-base font-semibold mt-1 mb-1">{section.label}</h4>
        </div>
      );
      section.fields.forEach((fieldObj: LegacyFormFieldObject) => {
        formItems.push(renderField(section, fieldObj));
      });
    });
  }

  return (
    <>
      <RecordDeleteDialog
        isOpen={showDeletionModal && !onDeleted}
        deletionInfo={deletionInfo}
        onClose={() => {
          setShowDeletionModal(false);
          if (onDeleted) onDeleted();
        }}
        onGoHome={() => {
          if (onDeleted) onDeleted();
          else navigate('/');
        }}
      />

      <DeleteConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title="Delete Record"
        message={`Are you sure you want to delete this ${metadata?.tableData?.byName?.[tableName]?.ui_label || tableName} record? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        waitSeconds={3}
        isDeleting={isDeleting}
      />

      <div
        className={`${hideChrome ? 'w-full' : 'w-full max-w-4xl ml-3'} ${manualSave && !isEditing ? 'sky-form-manual-view ' : ''}${className}`}
        style={{
          opacity: showDeletionModal ? 0.5 : isViewingDeletedRecord ? 0.6 : 1,
          pointerEvents: showDeletionModal || isViewingDeletedRecord ? 'none' : 'auto',
          position: 'relative'
        }}
      >
        {isViewingDeletedRecord && (
          <div className="sky-callout sky-callout-warning" style={{ marginBottom: '16px' }}>
            <h4 className="sky-callout-title">Record Deleted</h4>
            <p>
              This record has been deleted. You are viewing the last saved state from the audit log.
              All fields are read-only. If you need to restore it, please contact your administrator.
            </p>
          </div>
        )}

        <RecordDeleteBanner
          isDeleted={isDeleted}
          showDeletionModal={showDeletionModal}
          deletionInfo={deletionInfo}
        />

        {manualSave && !hideChrome && !isViewingDeletedRecord && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
            {editing ? (
              <>
                <button type="button" className="sky-btn sky-btn-ghost" onClick={discardEdits} disabled={isSavingForm}>Discard</button>
                <button type="button" className="sky-btn sky-btn-primary" onClick={() => void saveEdits()} disabled={isSavingForm}>
                  {isSavingForm ? 'Saving…' : 'Save changes'}
                </button>
              </>
            ) : (
              <button type="button" className="sky-btn sky-btn-ghost" onClick={() => setEditing(true)}>Edit</button>
            )}
          </div>
        )}

        {!hideChrome && !isViewingDeletedRecord && (!manualSave || editing) && (
          <RecordDeleteButton
            tableInfo={metadata?.tableData?.byName?.[tableName]}
            tableName={tableName}
            isDeleted={isDeleted}
            onDelete={handleDeleteClick}
            showDelete={tabConfig.showDelete}
          />
        )}

        {sectionVariant === 'cards' ? (
          <div className="sky-form-cards">{formItems}</div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '1rem'
            }}
          >
            {formItems}
          </div>
        )}

        <RecordWarningAlert
          warningState={warningState}
          onConfirm={confirmWarning}
          onCancel={cancelWarning}
        />
      </div>
    </>
  );
});

RecordDetailForm.displayName = 'RecordDetailForm';

export default RecordDetailForm;
